import { Controller } from "@hotwired/stimulus"
import { loadPdf } from "../helpers/load_pdf"
import PhotoSwipe from "photoswipe"
import { createPanoramaViewer, loadPanoramaTexture } from "../helpers/panorama_viewer"

// Slide type for equirectangular images. PhotoSwipe treats any type other than "image" as
// non-zoomable HTML content, which is exactly what we want: no pan/zoom transform fighting the
// sphere's own drag handling.
const PANORAMA_TYPE = "panorama"
// Equirectangular images are 2:1 by definition; only the ratio matters here, since a panorama
// slide is resized to fill the viewport rather than being fitted to its intrinsic size.
const PANORAMA_NOMINAL_SIZE = { width: 4096, height: 2048 }

export default class extends Controller {
  static targets = ["picture", "pdf"]

  connect() {
    this.panoramaViewers = new Map()
    // Canvases that already carry a thumbnail or have one rendering. The observer below fires
    // for every node added anywhere in the gallery — a progression form submit streams in several
    // — and pdf.js refuses a second render() on a canvas whose first is still running. Only a
    // canvas not seen before gets a render; resizing an existing one would also wipe its pixels.
    this.thumbnailCanvases = new WeakSet()
    this.renderPdfThumbnails()

    // Whenever the new node is added this function triggers renderPdfThumbnail
    // Used due to the turbo frame being used for new file
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          this.renderPdfThumbnails()
        }
      })
    })

    observer.observe(this.element, { childList: true, subtree: true })
    this.observer = observer
  }

  disconnect() {
    this.observer?.disconnect()
    // A Turbo navigation while the lightbox is open would otherwise leave the PhotoSwipe
    // DOM and its keyboard trap behind on the next page.
    this.pswp?.destroy()
    this.pswp = null
    this.destroyAllPanoramaViewers()
  }

  onImageClick(event) {
    event.preventDefault()
    const clicked = event.currentTarget
    const clickedSrc = clicked.getAttribute("href")

    this.loadItemsWithDimensions(clicked).then((items) => {
      if (!items.length) return
      // findIndex returns -1 when the clicked link isn't among the picture targets; clamping to
      // 0 keeps the lightbox usable instead of opening on nothing.
      const startIndex = Math.max(
        items.findIndex((item) => item.src === clickedSrc),
        0,
      )

      const pswp = new PhotoSwipe({
        dataSource: items,
        index: startIndex,
        bgOpacity: 0.8,
        showHideAnimationType: "none",
        trapFocus: false,
      })

      this.registerPanoramaSlides(pswp)
      this.pswp = pswp
      pswp.on("destroy", () => {
        if (this.pswp === pswp) this.pswp = null
      })
      pswp.init()
    })
  }

  // Builds the PhotoSwipe dataSource. `clicked` is a safety net: a partial that forgets the
  // picture target registers no targets at all, and without this the lightbox would open on an
  // empty dataSource.
  loadItemsWithDimensions(clicked) {
    const elements = this.pictureTargets.length ? this.pictureTargets : [clicked].filter(Boolean)

    const promises = elements.map((item) => {
      const src = item.getAttribute("href")
      const alt = item.getAttribute("title")

      if (item.dataset.galleryPanorama === "true") {
        // No preload: a 360 original can be 8–25 MB, and the viewer loads its own size-capped
        // variant anyway.
        return Promise.resolve({
          type: PANORAMA_TYPE,
          src,
          alt,
          panoramaSrc: item.dataset.galleryPanoramaSrc || src,
          ...PANORAMA_NOMINAL_SIZE,
        })
      }

      return new Promise((resolve) => {
        const img = new Image()
        img.src = src

        const done = () =>
          resolve({
            src,
            alt,
            width: img.naturalWidth || 1920,
            height: img.naturalHeight || 1080,
          })

        if (img.complete && img.naturalWidth) {
          done()
        } else {
          img.onload = done
          img.onerror = done
        }
      })
    })

    return Promise.all(promises)
  }

  // --- 360° slides ---

  registerPanoramaSlides(pswp) {
    const isPanorama = (content) => content?.data?.type === PANORAMA_TYPE

    // Take over content creation: PhotoSwipe would otherwise render this as an empty html slide.
    pswp.on("contentLoad", (event) => {
      if (!isPanorama(event.content)) return
      event.preventDefault()
      event.content.element = this.buildPanoramaMount()
    })

    // A panorama fills the viewport instead of being letterboxed to its 2:1 intrinsic size.
    pswp.on("contentResize", (event) => {
      if (!isPanorama(event.content)) return
      event.preventDefault()
      const element = event.content.element
      if (!element) return
      element.style.width = `${pswp.viewportSize.x}px`
      element.style.height = `${pswp.viewportSize.y}px`
      this.panoramaViewers.get(event.content)?.resize()
    })

    // Mount only for the slide actually on screen — PhotoSwipe preloads neighbours, and every
    // viewer costs a WebGL context (browsers cap those at roughly 16).
    pswp.on("contentActivate", (event) => {
      if (!isPanorama(event.content)) return
      this.mountPanoramaViewer(event.content)
    })

    pswp.on("contentDeactivate", (event) => this.destroyPanoramaViewer(event.content))
    pswp.on("contentDestroy", (event) => this.destroyPanoramaViewer(event.content))
    pswp.on("destroy", () => this.destroyAllPanoramaViewers())
  }

  buildPanoramaMount() {
    const element = document.createElement("div")
    element.className = "pswp__panorama"
    element.style.width = "100%"
    element.style.height = "100%"

    const status = document.createElement("div")
    status.className = "pswp__panorama-status"
    element.appendChild(status)

    return element
  }

  mountPanoramaViewer(content) {
    if (this.panoramaViewers.has(content)) return
    const element = content.element
    if (!element) return

    // Claim the slot synchronously so a fast deactivate/activate cycle can't mount twice while
    // the texture is still loading.
    this.panoramaViewers.set(content, null)

    loadPanoramaTexture(content.data.panoramaSrc)
      .then((texture) => {
        // Deactivated (or the lightbox closed) while the texture was in flight.
        if (!this.panoramaViewers.has(content)) {
          texture.dispose()
          return
        }
        element.querySelector(".pswp__panorama-status")?.remove()
        this.panoramaViewers.set(
          content,
          createPanoramaViewer(element, { texture, autoRotate: true, captureEvents: true }),
        )
      })
      .catch(() => {
        if (!this.panoramaViewers.has(content)) return
        this.panoramaViewers.delete(content)
        this.renderPanoramaFallback(element, content)
      })
  }

  // WebGL unavailable, or the texture failed to load: show the flat image rather than nothing.
  renderPanoramaFallback(element, content) {
    element.replaceChildren()
    const img = document.createElement("img")
    img.src = content.data.src
    img.alt = content.data.alt || ""
    img.className = "pswp__panorama-fallback"
    element.appendChild(img)
  }

  destroyPanoramaViewer(content) {
    if (!this.panoramaViewers.has(content)) return
    this.panoramaViewers.get(content)?.destroy()
    this.panoramaViewers.delete(content)
  }

  destroyAllPanoramaViewers() {
    this.panoramaViewers?.forEach((viewer) => viewer?.destroy())
    this.panoramaViewers?.clear()
  }

  // --- PDF thumbnails ---

  renderPdfThumbnails() {
    const pdfLinks = this.element.querySelectorAll("#pdf-preview")
    pdfLinks.forEach((link) => {
      const canvas = link.querySelector("canvas")
      if (!canvas || this.thumbnailCanvases.has(canvas)) return
      this.thumbnailCanvases.add(canvas)

      const url = link.getAttribute("href")

      const parentAnchor = link.parentElement
      const width = parentAnchor.clientWidth > 0 ? parentAnchor.clientWidth : 64
      const height = parentAnchor.clientHeight > 0 ? parentAnchor.clientHeight : 45
      canvas.width = width
      canvas.height = height

      this.renderPdfThumbnail(url, canvas)
    })
  }

  async renderPdfThumbnail(url, canvas) {
    let pdf
    try {
      pdf = await loadPdf(url)
      const page = await pdf.getPage(1)
      const viewport = page.getViewport({ scale: 0.5 })
      const context = canvas.getContext("2d")

      const scale = Math.min(canvas.width / viewport.width, canvas.height / viewport.height)

      const scaledViewport = page.getViewport({ scale })

      context.clearRect(0, 0, canvas.width, canvas.height)

      await page.render({ canvasContext: context, viewport: scaledViewport }).promise
    } catch (error) {
      // Let a later mutation try this canvas again, e.g. after a dropped request.
      this.thumbnailCanvases.delete(canvas)
      console.error("Error rendering PDF thumbnail:", error)
    } finally {
      // Each document holds its own pdf.js worker; the thumbnail no longer needs it. Released through
      // its loading task: PDFDocumentProxy#destroy is gone from the pdf.js we ship.
      pdf?.loadingTask.destroy().catch(() => {})
    }
  }
}
